import {
  array,
  boolean,
  InferInput,
  InferOutput,
  literal,
  number,
  object,
  optional,
  pipe,
  safeParse,
  string,
  transform,
  ValiError,
} from 'valibot';
import { AMusicError, trimQueue } from '../utils.ts';

const RestoreRequestArgsSchema = pipe(
  object({
    id: optional(string()),
    queue: array(object({
      id: string(),
      type: literal('track'),
    })),
    position: number(),
    progress: number(),
    isPlaying: boolean(),
  }),
  transform(({ id, queue, position, progress, isPlaying }) => ({
    type: id ? 'station' as const : 'track' as const,
    id,
    queue,
    position,
    progress,
    isPlaying,
  })),
);

export type RestoreRequestArgs = InferInput<typeof RestoreRequestArgsSchema>;

const toSecondsByMediaItem = (
  queueItem: MusicKit.MediaItem,
  progress: number,
): number => {
  const durationInMillis = queueItem?.attributes?.durationInMillis ?? 0;
  return (durationInMillis / 1000) * progress;
};

/**
 * Restore songs in the queue to a specific position with progress and playback state.
 * @param {MusicKit.MusicKitInstance} player The MusicKit player instance.
 * @param {InferOutput<typeof RestoreTrackRequestArgsSchema>} args The arguments for restoring tracks.
 * @returns {Promise<void>} A promise that resolves when the tracks are restored.
 */
async function restoreTracks(
  player: MusicKit.MusicKitInstance,
  args: InferOutput<typeof RestoreRequestArgsSchema>,
): Promise<void> {
  const { queue, position, progress, isPlaying } = args;

  const songsQueue = queue.map((item) => item.id);
  await player.setQueue({ songs: songsQueue });
  const restoredQueue = player.queue.items;
  const queueItem = restoredQueue[position];
  if (!queueItem) {
    throw new AMusicError(
      'Queue item not found at the specified position',
      AMusicError.NOT_FOUND,
    );
  }
  await player.changeToMediaAtIndex(position);

  const currentTime = toSecondsByMediaItem(queueItem, progress);
  await player.seekToTime(currentTime);
  if (isPlaying) {
    await player.play();
  } else {
    await player.pause();
  }
}

/**
 * Restore a station by its ID and playback state.
 * @param {MusicKit.MusicKitInstance} player The MusicKit player instance.
 * @param {InferOutput<typeof RestoreStationRequestArgsSchema>} args The arguments for restoring a station.
 * @returns {Promise<void>} A promise that resolves when the station is restored.
 */
async function restoreStation(
  player: MusicKit.MusicKitInstance,
  args: InferOutput<typeof RestoreRequestArgsSchema>,
): Promise<void> {
  const { id, queue, position, progress, isPlaying } = args;

  const $restoreStation = async (
    player: MusicKit.MusicKitInstance,
    id: string,
    isPlaying: boolean,
  ) => {
    await player.setQueue(
      { station: id } as unknown as MusicKit.SetQueueOptions,
    );
    if (isPlaying) {
      await player.play();
    } else {
      await player.pause();
    }
  };

  // Trim the queue to only include items after the specified position.
  const _stationSongQueue = (() => {
    try {
      const _queue = trimQueue(queue, position);
      if (_queue.length > 0) {
        return _queue.splice(0, 1); // Get the first item in the queue.
      }
      return [];
    } catch {
      return [];
    }
  })();

  // If there are no songs in the queue, we can just set the station queue, and play/pause it.
  if (_stationSongQueue.length === 0) {
    await $restoreStation(player, id!, isPlaying);
    return;
  }

  // If there are songs in the queue, we need to set the queue with the songs.
  // This is a workaround for the MusicKit API, which does not allow setting a station with a queue.
  const stationSongQueue = _stationSongQueue.map((item) => item.id);

  // Set the queue with the ONE song. stationsSongQueue.length should always be 1.
  await player.setQueue({ songs: stationSongQueue });

  // If the placeback state is completed, we need to set the station again.
  const playbackStateDidChange = async (
    e: { oldState: MusicKit.PlaybackStates; state: MusicKit.PlaybackStates },
  ) => {
    if (e.state !== MusicKit.PlaybackStates.completed) {
      return;
    }

    removeLocalListeners(player);

    // If the playback state is completed, we need to restore the station.
    await $restoreStation(player, id!, isPlaying);
  };

  // Add an event listener for playback state changes to handle the completed state.
  (player as MusicKit.MusicKitInstance).addEventListener(
    'playbackStateDidChange',
    playbackStateDidChange,
  );

  const queueItemsDidChange = (e: unknown) => {
    const { items } = e as { items: MusicKit.MediaItem[] | undefined };

    if (!Array.isArray(items) || items.length === 0) {
      removeLocalListeners(player);
      return;
    }
    if (items.length === 1 && items[0].id === stationSongQueue[0]) {
      // If the first item in the queue is the same as the one we restored, we can skip removing the event listeners.
      return;
    }
    if (items.length > 1 || items[0].id !== stationSongQueue[0]) {
      // If there are more than one item in the queue, we need to remove the event listeners.
      // Cancelling restoration of the station queue due to queue changes.
      removeLocalListeners(player);
      return;
    }
  };

  (player as MusicKit.MusicKitInstance).addEventListener(
    'queueItemsDidChange',
    queueItemsDidChange,
  );

  const removeLocalListeners = (player: MusicKit.MusicKitInstance) => {
    player.removeEventListener(
      'playbackStateDidChange',
      playbackStateDidChange,
    );
    player.removeEventListener(
      'queueItemsDidChange',
      queueItemsDidChange,
    );
  };

  const restoredQueue = player.queue.items;
  const queueItem = restoredQueue[0]; // Always the first item in a station song queue.
  await player.changeToMediaAtIndex(0);
  const currentTime = toSecondsByMediaItem(queueItem, progress);
  await player.seekToTime(currentTime);

  if (isPlaying) {
    await player.play();
  } else {
    await player.pause();
  }
}

/**
 * Restore a player's state based on the provided arguments.
 * @param {MusicKit.MusicKitInstance} player The MusicKit player instance.
 * @param {RestoreRequestArgs} args The arguments for restoring the player's state.
 * @returns {Promise<void>} A promise that resolves when the player's state is restored.
 */
export const $restore = async (
  player: MusicKit.MusicKitInstance,
  args: RestoreRequestArgs,
): Promise<void> => {
  if (!player) {
    throw new Error('Player is not initialized');
  }

  const parsedArgs = safeParse(RestoreRequestArgsSchema, args);
  if (!parsedArgs.success) {
    throw new ValiError(parsedArgs.issues);
  }

  const p = parsedArgs.output;

  if (p.type === 'track') {
    await restoreTracks(player, p);
  } else if (p.type === 'station') {
    await restoreStation(player, p);
  } else {
    throw new AMusicError(
      'Invalid restore request type',
      AMusicError.UNSUPPORTED_ERROR,
    );
  }
};
